import React, { useState, useEffect } from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Table, Space, Tag, message, Select, Modal, Input, Typography, Divider } from "antd";
import axios from "axios";
import Constants from "../Utils/Constants";
import useToken from "../Authentication/useToken";
import { useTranslation } from "react-i18next";

export default function AccessRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");

  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const [contactSubject, setContactSubject] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  const [contactHistory, setContactHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [contactSending, setContactSending] = useState(false);

  const { token } = useToken();
  const { t, i18n } = useTranslation("AccessRequests");
  const { Text } = Typography;
  const { TextArea } = Input;

  const fetchRequests = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${Constants.SERVER_URL}/access-requests`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      setRequests(res.data);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchRequests();
    }
  }, [token]);

  useEffect(() => {
    const fetchCurrentUser = async() => {
      try {
        const res = await axios.get(
          `${Constants.SERVER_URL}/access-requests/users/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        setCurrentUser(res.data);
      } catch (err) {
        console.error("Error fetching current user:", err);
      }
    };
    if (token) {
      fetchCurrentUser();
    }
  }, [token]);

  const approveRequest = async (id) => {
  try {
    const res = await axios.patch(
      `${Constants.SERVER_URL}/access-requests/${id}/approve`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    message.success(t(res.data?.message || "request_approved"));

    await fetchRequests();

  } catch (err) {
    const backendMessage = err.response?.data?.message || "error_approving_request";
    message.error(t(backendMessage));

    console.error(err);
  }
  };

  const rejectRequest = async (id) => {
  try {

    const res = await axios.patch(
      `${Constants.SERVER_URL}/access-requests/${id}/reject`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    message.success(t(res.data?.message || "request_rejected"));

    await fetchRequests();

  } catch (err) {
    const backendMessage = err.response?.data?.message || "error_rejecting_request";
    message.error(t(backendMessage));
    
    console.error(err);
  }
  };

  const openContactModal = (request) => {
    setSelectedRequest(request);
    setContactSubject("");
    setContactMessage("");
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
    setSelectedRequest(null);
    setContactSubject("");
    setContactMessage("");
  };

  const openHistoryModal = async (request) => {
    try {
      setSelectedRequest(request);
      setIsHistoryModalOpen(true);
      setHistoryLoading(true);

      const res = await axios.get(
        `${Constants.SERVER_URL}/access-requests/${request.id}/contact-history`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setContactHistory(res.data || []);
    } catch (err) {
      const backendMessage =
        err.response?.data?.message || "error_fetching_contact_history";
      message.error(t(backendMessage));
      console.error(err);
      setContactHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const closedHistoryModal = () => {
    setIsHistoryModalOpen(false);
    setSelectedRequest(null);
    setContactHistory([]);
  };

  const sendContactMessage = async () => {
    try {
      if (!selectedRequest) return;

      if (!contactSubject.trim() || !contactMessage.trim()) {
        message.error(t("missing_contact_fields"));
        return;
      }
      
      setContactSending(true);

      const res = await axios.post(
        `${Constants.SERVER_URL}/access-requests/${selectedRequest.id}/contact`,
        {
          subject: contactSubject,
          messageBody: contactMessage,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      message.success(t(res.data?.message || "contact_sent_and_saved"));
      closeContactModal();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message || "error_sending_contact_message";
      message.error(t(backendMessage));
      console.error(err);
    } finally {
      setContactSending(false);
    }
  };

  const columns = [
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_name")}</span>,
    render: (_, record) => record ? `${record.firstName} ${record.lastName}` : "",
    // fixed: i18n.language === "ar" ? "right" : "left",
    width: 130,
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_contact")}</span>,
    width: 200,
    render: (_, record) => (
      <div style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
        <div>{record.email || "-"}</div>
        <div>{record.phone || "-"}</div>
      </div>
    ),
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_role")}</span>,
    dataIndex: "role",
    width: 140,
    render: (role) => {
      if (role === "CLINICADMIN") return t("role_clinic_admin");
      if (role === "KINESIOLOGIST") return t("role_kinesiologist");
      if (role === "DOCTOR") return t("role_doctor");
    }
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_admin")}</span>,
    dataIndex: "isAdmin",
    width: 70,
    align: "center",
    render: (isAdmin) => {
      if (isAdmin === "YES") return <CheckOutlined />;
      if (isAdmin === "NO") return <CloseOutlined />;
    }
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_organization")}</span>,
    width: 200,
    render: (_, record) => (
      <div style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
        <div>{record.clinicName || "-"}</div>
        <div style={{ color: "#666" }}>{`(${record.clinicType})` || "-"}</div>
      </div>
    ),
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_clinic_address")}</span>,
    render: (_, record) => (
      <div style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
        <div>{record.clinicAddressLine || "-"}</div>
        <div>
          {[record.clinicCity, record.clinicProvince]
            .filter(Boolean)
            .join(", ") || "-"}
        </div>
        <div>{record.clinicPostalCode || "-"}</div>
      </div>
    ),
    width: 200,
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_organization_contact")}</span>,
    width: 200,
    render: (_, record) => (
      <div style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
        <div>{record.clinicEmail || "-"}</div>
        <div>{record.clinicPhone || "-"}</div>
      </div>
    ),
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_status")}</span>,
    dataIndex: "status",
    width: 70,
    align: "center",
    render: (status) => {
      if (status === "PENDING") return <Tag color="orange">{t("status_pending")}</Tag>;
      if (status === "APPROVED") return <Tag color="green">{t("status_approved")}</Tag>;
      if (status === "REJECTED") return <Tag color="red">{t("status_rejected")}</Tag>;
    }
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_actions")}</span>,
    width: 90,
    align: "center",
    render: (_, record) => (
      <Space direction="vertical" size="small">
        <Space direction="horizontal" size="small">
        <Button
          type="primary"
          disabled={record.status !== "PENDING"}
          onClick={() => approveRequest(record.id)}
        >
          {t("button_approve")}
        </Button>

        <Button
          danger
          disabled={record.status !== "PENDING"}
          onClick={() => rejectRequest(record.id)}
        >
          {t("button_reject")}
        </Button>
        </Space>
        <Button 
          disabled={record.status !== "PENDING"}
          onClick={() => openContactModal(record)}
        >
          {t("button_contact")}
        </Button>

        <Button type="secondary" style={{ textDecoration: "underline", color: "gray" }} onClick={() => openHistoryModal(record)}>
          {t("button_contact_history")}
        </Button>
      </Space>
    )
  },
  {
    title: <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>{t("title_processed")}</span>,
    width: 130,
    render: (_, record) => (
      <div style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
        <div>
          {record.reviewer
            ? `${record.reviewer.firstname} ${record.reviewer.lastname}`
            : "-"}
        </div>
        <div style={{ color: "#666" }}>
          {record.reviewedAt
            ? new Date(record.reviewedAt).toLocaleDateString()
            : "-"}
        </div>
      </div>
    ),
  }
  ];

  const filteredRequests = statusFilter === "ALL" ? requests : requests.filter((request) => request.status === statusFilter);

  return (

  <div>

    <h2>{t("title_access_requests")}</h2>

    <div style={{     
        marginRight: i18n.language === "ar" ? 0 : "24px",
        marginLeft: i18n.language === "ar" ? "24px" : 0
      }}>
    <div style={{ marginBottom: "16px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", alignItems: "center" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <span>{t("label_filter_status")}</span>
      <Select
        value={statusFilter}
        onChange={setStatusFilter}
        style={{ width: 200 }}
      >
        <Select.Option value="ALL">{t("filter_all")}</Select.Option>
        <Select.Option value="PENDING">{t("status_pending")}</Select.Option>
        <Select.Option value="APPROVED">{t("status_approved")}</Select.Option>
        <Select.Option value="REJECTED">{t("status_rejected")}</Select.Option>
      </Select>
      </div>
    </div>
    <Table
      rowKey="id"
      columns={columns}
      dataSource={filteredRequests || []}
      loading={loading}
      locale={{ emptyText: t("message_no_requests_yet") }}
      scroll={{ x: "max-content" }}
      pagination={{
        pageSize: 10,
        showSizeChanger: false,
        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`
      }}
    />

      <Modal
        title={t("title_contact_modal")}
        open={isContactModalOpen}
        onCancel={closeContactModal}
        onOk={sendContactMessage}
        confirmLoading={contactSending}
        okText={t("button_send")}
        cancelText={t("button_cancel")}
        width={window.innerWidth < 768 ? "90%": 800}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxHeight: "450px", overflowY: "auto" }}>
          <div>
            <Text strong>{t("label_sender_email")}</Text>
            <Input 
              value={currentUser?.email || ""}
              disabled
            />
          </div>

          <div>
            <Text strong>{t("label_receiver_email")}</Text>
            <Input
              value={selectedRequest?.email || ""}
              disabled
            />
          </div>

          <div>
            <Text strong>{t("label_subject")}</Text>
            <Input
              value={contactSubject}
              onChange={(e) => setContactSubject(e.target.value)}
              placeholder={t("placeholder_contact_subject")}
            />
          </div>

          <div>
            <Text strong>{t("label_message")}</Text>
            <TextArea
              rows={6}
              value={contactMessage}
              onChange={(e) => setContactMessage(e.target.value)}
              placeholder={t("placeholder_contact_message")}
            />
          </div>
        </div>
      </Modal>

      <Modal
        title={t("title_contact_history_modal")}
        open={isHistoryModalOpen}
        onCancel={closedHistoryModal}
        footer={[
          <Button key="close" onClick={closedHistoryModal}>
            {t("button_close")}
          </Button>
        ]}
        width={window.innerWidth < 768 ? "90%": 800}
      >
        {historyLoading ? (
          <p>{t("message_loading_history")}</p>
        ) : contactHistory.length === 0 ? (
          <p>{t("message_no_contact_history")}</p>
        ) : (
          <div style={{ maxHeight: "450px", overflowY: "auto" }}>
            {contactHistory.map((entry, index) => (
              <div key={entry.id || index} style={{ marginBottom: "16px" }}>
              <div>
                <Text strong>{t("label_sender")}:</Text>{" "}
                {entry.senderAdmin
                ? `${entry.senderAdmin.firstname} ${entry.senderAdmin.lastname} (${entry.senderEmail})`
                : entry.senderEmail || "-"}
              </div>

              <div>
                <Text strong>{t("label_receiver")}:</Text>{" "}
                {entry.receiverEmail || "-"}
              </div>

              <div>
                <Text strong>{t("label_subject")}:</Text>{" "}
                {entry.subject || "-"}
              </div>
              
              <div>
                <Text strong>{t("label_sent_at")}:</Text>{" "}
                {entry.sentAt ? new Date(entry.sentAt).toLocaleDateString() : "-"}
              </div>

              <div style={{ marginTop: "8px", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                <Text strong>{t("label_message")}:</Text>
                <div>{entry.messageBody || "-"}</div>
              </div>

              {index < contactHistory.length -1 && <Divider />}
              </div>

            ))}
          </div>
        )}
        </Modal>

    </div>

  </div>

  );

}
